
import { supabaseWorker } from '@/integrations/supabase/workerClient';
import { supabase } from '@/integrations/supabase/client';
import { MessageCache } from './messageCache';

export interface UnreadMessagesResponse {
  total_count: number;
  conversations: Array<{
    conversation_id: string;
    unread_count: number;
    last_message: string;
    last_message_time: string;
    source: string;
    client_info?: {
      name: string;
      phone?: string;
    };
  }>;
}

type ConversationRow = {
  id: string;
  source: string;
  created_at: string;
  client?: { full_name?: string; phone?: string };
};

/**
 * Handles counting and managing unread message statistics
 */
export class MessageCounter {
  // Session-scoped flag to avoid repeated RPC 404 noise
  // Default is false to fully disable RPC attempts and rely on fallbacks.
  // Can be flipped to true in future when RPCs are deployed.
  private static rpcAvailable: boolean | null = false;

  private static isRpcMissingError(err: unknown): boolean {
    const msg = (err && (err as { message?: unknown }).message) as string | undefined;
    // PostgREST 404 typically yields statusText 'Not Found' or message including 404
    return typeof msg === 'string' && (msg.includes('Not Found') || msg.includes('404'));
  }
  /**
   * Resolve current user id using supabase auth (preferred),
   * fallback to parsing sb-<ref>-auth-token if needed.
   */
  private static async getCurrentUserId(): Promise<string | null> {
    try {
      const { data } = await supabase.auth.getUser();
      if (data?.user?.id) return data.user.id;
    } catch (e) {
      // ignore and try storage-based fallback
    }

    try {
      // Fallback: attempt to read standard sb-<ref>-auth-token
      const url = import.meta.env.VITE_SUPABASE_URL || 'https://aoucmkkqfbqccmcerwtr.supabase.co';
      const { hostname } = new URL(url);
      const [ref] = hostname.split('.') as [string, ...string[]];
      const storageKey = ref ? `sb-${ref}-auth-token` : null;
      if (storageKey) {
        const raw = localStorage.getItem(storageKey);
        if (raw) {
          const parsed = JSON.parse(raw);
          const id = parsed?.user?.id as string | undefined;
          if (id) return id;
        }
      }
    } catch (e) {
      // ignore
    }
    return null;
  }
  /**
   * Gets the total number of unread messages for the current user
   */
  static async getUnreadMessageCount(): Promise<number> {
    // Check cache first
    const cachedCount = MessageCache.getCachedCount();
    if (cachedCount !== null) {
      return cachedCount;
    }

    try {
      const userId = await MessageCounter.getCurrentUserId();
      if (!userId) return 0;
      // Try RPC only if not previously marked missing
      if (MessageCounter.rpcAvailable !== false) {
        const { data, error } = await supabaseWorker.rpc('get_unread_messages_count', { p_user_id: userId });

        if (!error && data) {
          MessageCounter.rpcAvailable = true;
          const unreadCount = (data as { count?: unknown } | null | undefined)?.count;
          const countNum = typeof unreadCount === 'number' ? unreadCount : Number(unreadCount) || 0;
          MessageCache.updateCache(countNum);
          return countNum;
        }

        // Any RPC error -> disable further attempts to stop network 404 spam
        if (error) {
          MessageCounter.rpcAvailable = false;
          if (import.meta.env.DEV) {
            console.info('[MessageCounter] RPC disabled after first failure; using fallbacks only');
          }
        }
      }

      // Fallback path (also used when RPC disabled)
      {
        // Fallback: fetch unread incoming messages and count
        const { data: unreadMessages, error: fallbackError } = await supabaseWorker
          .from('messages')
          .select('id')
          .eq('direction', 'incoming')
          .eq('is_read', false);
        if (fallbackError || !unreadMessages) {
          console.error('[MessageCounter] Fallback unread count failed:', fallbackError);
          return 0;
        }
        const unreadArr = (unreadMessages as unknown[] | null | undefined) ?? [];
        const unreadCount = unreadArr.length;
        MessageCache.updateCache(unreadCount);
        return unreadCount;
      }
    } catch (error) {
      console.error('[MessageCounter] Error in getUnreadMessageCount:', error);
      return 0;
    }
  }

  /**
   * Gets detailed information about unread messages grouped by conversation
   */
  static async getUnreadMessagesDetail(): Promise<UnreadMessagesResponse> {
    try {
      const userId = await MessageCounter.getCurrentUserId();
      if (!userId) {
        return { total_count: 0, conversations: [] };
      }

      // Try RPC only if not disabled
      if (MessageCounter.rpcAvailable !== false) {
        const { data, error } = await supabaseWorker.rpc('get_unread_messages_by_conversation', { p_user_id: userId });

        if (!error && data) {
          MessageCounter.rpcAvailable = true;
          type RpcConversation = {
            id: string;
            unread_count: number;
            last_message: string;
            last_message_time: string;
            source: string;
            client?: { full_name?: string | null; phone?: string | null } | null;
          };

          const rpcRows = (data as unknown[]) as RpcConversation[];
          const conversationDetails = rpcRows.map((conversation) => {
            return {
              conversation_id: conversation.id,
              unread_count: conversation.unread_count,
              last_message: conversation.last_message,
              last_message_time: conversation.last_message_time,
              source: conversation.source,
              client_info: conversation.client ? {
                name: conversation.client.full_name || `${conversation.source} пользователь`,
                phone: conversation.client.phone
              } : undefined
            };
          });

          const unreadConversations = conversationDetails.filter(conv => conv.unread_count > 0);
          const totalUnread = unreadConversations.reduce((sum, conv) => sum + conv.unread_count, 0);
          MessageCache.updateCache(totalUnread);
          return { total_count: totalUnread, conversations: unreadConversations };
        }

        // Any RPC error -> disable further attempts to stop network 404 spam
        if (error) {
          MessageCounter.rpcAvailable = false;
          if (import.meta.env.DEV) {
            console.info('[MessageCounter] RPC (detail) disabled after first failure; using fallbacks only');
          }
        }
      }

      // Fallback aggregation
      {
        // Fallback: fetch unread incoming messages and aggregate on client
        const { data: unreadMessages, error: fallbackError } = await supabaseWorker
          .from('messages')
          // 'messages' table uses column name 'message' (not 'content') per schema
          .select('id, conversation_id, message, created_at, source, client_id')
          .eq('direction', 'incoming')
          .eq('is_read', false);
        if (fallbackError || !unreadMessages) {
          console.error('[MessageCounter] Fallback unread detail failed:', fallbackError);
          return { total_count: 0, conversations: [] };
        }

        // Define row shapes
        type FallbackMessageRow = {
          id: string;
          conversation_id: string | null;
          message: string | null;
          created_at: string;
          source: string | null;
          client_id: string | null;
        };
        type ClientBrief = { id: string; full_name: string | null; phone: string | null };

        const fallbackRows = (unreadMessages as unknown[]) as FallbackMessageRow[];

        // Fetch clients for name resolution (distinct ids)
        const clientIds = Array.from(new Set(fallbackRows.map((m) => m.client_id).filter((v): v is string => Boolean(v))));
        const clientMap = new Map<string, { full_name?: string | null; phone?: string | null }>();
        if (clientIds.length > 0) {
          const { data: clients } = await supabaseWorker
            .from('clients')
            .select('id, full_name, phone')
            .in('id', clientIds);
          (clients as unknown[] | null | undefined ?? []).forEach((c) => {
            const row = c as ClientBrief;
            clientMap.set(row.id, { full_name: row.full_name, phone: row.phone });
          });
        }

        const byConversation = new Map<string, { conversation_id: string; unread_count: number; last_message: string; last_message_time: string; source: string; client_id?: string | null }>();
        fallbackRows.forEach((m) => {
          const key = m.conversation_id || 'unknown';
          const existing = byConversation.get(key);
          if (!existing) {
            byConversation.set(key, {
              conversation_id: key,
              unread_count: 1,
              last_message: m.message || '',
              last_message_time: m.created_at,
              source: m.source || 'unknown',
              client_id: m.client_id || null
            });
          } else {
            existing.unread_count += 1;
            if (new Date(m.created_at) > new Date(existing.last_message_time)) {
              existing.last_message = m.message || existing.last_message;
              existing.last_message_time = m.created_at;
            }
          }
        });

        const conversations = Array.from(byConversation.values()).map(conv => {
          const info = conv.client_id ? clientMap.get(conv.client_id) : undefined;
          return {
            conversation_id: conv.conversation_id,
            unread_count: conv.unread_count,
            last_message: conv.last_message,
            last_message_time: conv.last_message_time,
            source: conv.source,
            client_info: info ? {
              name: info.full_name || `${conv.source} пользователь`,
              phone: info.phone || undefined
            } : undefined
          };
        });

        const totalUnread = conversations.reduce((sum, c) => sum + c.unread_count, 0);
        MessageCache.updateCache(totalUnread);
        return { total_count: totalUnread, conversations };
      }

      
      
    } catch (error) {
      console.error('[MessageCounter] Error in getUnreadMessagesDetail:', error);
      return { total_count: 0, conversations: [] };
    }
  }
}
